//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

/** @jsxImportSource @emotion/react */

// NOTE: This file uses Emotion CSS and should be migrated to Tailwind CSS.
// Dark mode colors reference Carbon design tokens defined in tailwind.config.js:
// - #262626 (carbon.darkPanel)

export const navBarStyle = {
  navBar: (isZoomedIn: boolean, darkMode = false) => ({
    height: '48px',
    backgroundColor: darkMode ? '#262626' : '#0F62FE',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: 'white',
    transition: 'background-color 0.3s ease',
    position: 'fixed' as const,
    top: 0,
    width: '100%',
    zIndex: 1300,
    boxShadow: '0px 2px 4px 0px #00000014',
    margin: 0,
    padding: isZoomedIn ? '0 10px 0 20px' : undefined,
    a: {
      textDecoration: 'none',
    },
    '@media (maxWidth: 1200px)': {
      padding: '0 16px',
    },
    '@media (maxWidth: 768px)': {
      padding: '0 8px',
    },
  }),
  supportOptionsBox: {
    width: '100%',
    maxWidth: 260,
    bgcolor: 'background.paper',
    color: 'black',
  },
  logo: {
    height: '20px',
    width: '20px',
    zIndex: 999,
    cursor: 'pointer',
    '@media (maxWidth: 3840px)': {
      height: '22px',
      marginLeft: '15px',
    },
    '@media (maxWidth: 2560px)': {
      height: '20px',
      marginLeft: '20px',
    },
    '@media (maxWidth: 1920px)': {
      height: '18px',
      marginLeft: '18px',
    },
    '@media (maxWidth: 1366px)': {
      height: '16px',
      marginLeft: '15px',
    },
    '@media (maxWidth: 1024px)': {
      height: '14px',
      marginLeft: '12px',
    },
    '@media (maxWidth: 800px)': {
      height: '12px',
      marginLeft: '10px',
    },
  },
  rightLogos: {
    marginBottom: 'none',
    position: 'relative' as const,
    marginRight: '10px',
  },
  rightlogo: {
    height: '23px',
    width: '23px',
    marginLeft: '28px',
    marginBottom: 0,
    marginTop: '4px',
    cursor: 'pointer',
    '@media (maxWidth: 1200px)': {
      height: '32px',
      marginRight: '8px',
      marginLeft: '12px',
    },
    '@media (maxWidth: 768px)': {
      height: '24px',
      marginRight: '4px',
      marginLeft: '8px',
    },
  },
  loginLogo: {
    height: '26px',
    width: '26px',
    marginLeft: '28px',
    marginBottom: '-2px',
    marginTop: '4px',
    marginRight: '40px',
    cursor: 'pointer',
    borderRadius: '5%',

    '@media (maxWidth: 1200px)': {
      height: '32px',
      marginRight: '8px',
      marginLeft: '12px',
    },
    '@media (maxWidth: 768px)': {
      height: '24px',
      marginRight: '4px',
      marginLeft: '8px',
    },
  },
  supportLogo: {
    height: '20px',
    width: '28px',
    marginLeft: '28px',
    marginBottom: 0,
    marginTop: '4px',
    cursor: 'pointer',
    '@media (maxWidth: 1200px)': {
      height: '32px',
      marginLeft: '12px',
    },
    '@media (maxWidth: 768px)': {
      height: '24px',
      marginLeft: '8px',
    },
  },
  navText: {
    fontSize: '22px',
    cursor: 'pointer',
    height: '27px',
    maxHeight: '80%',
    maxWidth: '100%',
    marginLeft: '25px',
    color: '#FFFFFF',
    fontWeight: 800,
    fontFamily: 'Inter',
    paddingLeft: '5px',
    lineHeight: '27px',
    textAlign: 'center' as const,
    '@media (maxWidth: 1200px)': {
      fontSize: '32px',
    },
    '@media (maxWidth: 768px)': {
      fontSize: '24px',
    },
  },
  logo4: {
    width: '50px',
    height: '35px',
    '@media (maxWidth: 1200px)': {
      width: '40px',
      height: '32px',
    },
    '@media (maxWidth: 768px)': {
      width: '32px',
      height: '24px',
    },
  },
  DD: {
    width: '40px',
    height: '40px',
    gap: '0px',
    paddingRight: '8px',
    '@media (maxWidth: 1200px)': {
      height: '24px',
    },
    '@media (maxWidth: 768px)': {
      height: '20px',
    },
  },
  dropdownContainer: {
    position: 'relative' as const,
    zIndex: 999,
  },
  dropdownOptions: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  dropdown: {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'space-between',
    position: 'absolute' as const,
    marginTop: '48px',
    marginLeft: '8px',
    fontFamily: 'Inter',
    transform: 'translateY(3px)',
    width: '320px',
    background: 'rgb(56, 56, 56)',
    padding: '16px',
    borderRadius: '16px',
    '::before': {
      content: "''",
      position: 'absolute' as const,
      top: '-20px',
      left: '12%',
      borderWidth: '10px',
      borderStyle: 'solid',
      borderColor: 'transparent transparent rgb(56, 56, 56) transparent',
    },
    '@media (maxWidth: 1200px)': {
      width: '240px',
    },
    '@media (maxWidth: 768px)': {
      width: '200px',
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column' as const,
    width: '50%',
  },
  item: {
    marginBottom: '10px',
    width: '100%',
  },
  button: {
    width: '100%',
    height: '50px',
    background: 'rgb(56, 56, 56)',
    fontFamily: 'Roboto',
    textDecoration: 'none',
    border: 'none',
    padding: 0,
    textAlign: 'left' as const,
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '16.41px',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: 'gray',
      cursor: 'pointer',
    },
    '@media (maxWidth: 1200px)': {
      height: '40px',
    },
    '@media (maxWidth: 768px)': {
      height: '32px',
    },
  },
  line: {
    borderTop: '1px solid white',
    width: '100%',
  },
  logoHighlighted: {
    height: '20px',
    width: '20px',
    zIndex: 999,
    cursor: 'pointer',
    '@media (maxWidth: 3840px)': {
      height: '22px',
      marginLeft: '15px',
    },
    '@media (maxWidth: 2560px)': {
      height: '20px',
      marginLeft: '20px',
    },
    '@media (maxWidth: 1920px)': {
      height: '18px',
      marginLeft: '18px',
    },
    '@media (maxWidth: 1366px)': {
      height: '16px',
      marginLeft: '15px',
    },
    '@media (maxWidth: 1024px)': {
      height: '14px',
      marginLeft: '12px',
    },
    '@media (maxWidth: 800px)': {
      height: '12px',
      marginLeft: '10px',
    },
  },
  centeredText: {
    color: 'white',
    textAlign: 'left' as const,
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '16.41px',
    marginLeft: '50px',
    textDecoration: 'none',
  },
  infoDropdown: {
    position: 'absolute' as const,
    right: 0,
    marginRight: '55px',
    top: '100%',
    background: 'rgb(56, 56, 56)',
    border: '1px solid black',
    padding: '10px',
    zIndex: 11,
    width: 'max-content',
    fontSize: '14px',
    fontFamily: 'Inter',
    '@media (maxWidth: 1200px)': {
      width: '100%',
      left: 0,
    },
    '@media (maxWidth: 768px)': {
      width: '100%',
      left: 0,
    },
  },
  profileDropdown: {
    position: 'absolute' as const,
    marginRight: '40px',
    marginTop: '-5px',
    paddingTop: '0px',
    right: '0',
    left: 'auto',
    background: 'rgb(232, 230, 230)',
    boxShadow: '0px 4px 4px 0px #00000040',
    fontFamily: 'Inter',
    fontSize: '14px',
    zIndex: 11,
    width: 'max-content',
    cursor: 'pointer',
    '@media (maxWidth: 1200px)': {
      width: '100%',
      left: 0,
    },
    '@media (maxWidth: 768px)': {
      width: '100%',
      left: 0,
    },
  },
  supportDropdown: (isZoomedIn: boolean) => ({
    top: '100%',
    position: 'absolute' as const,
    marginRight: '64px',
    left: 0,
    right: 0,
    background: 'rgb(255, 255, 255)',
    fontFamily: 'Inter',
    fontSize: '14px',
    zIndex: 11,
    width: 'max-content',
    boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.4)',
    transform: isZoomedIn
      ? 'translateX(120%) translateY(-10%)'
      : 'translateX(-50%) translateY(0px)',
    '@media (maxWidth: 1200px)': {
      width: '100%',
      left: 0,
    },
    '@media (maxWidth: 768px)': {
      width: '100%',
      left: 0,
    },
  }),
  LogoutButton: {
    backgroundColor: 'white',
    color: '#5c5c5c',
    border: 'none',
    cursor: 'pointer',
    alignContent: 'left',
    padding: '0',
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '13.31px',
    width: '100%',
    display: 'flex',
    paddingRight: '50px',
    paddingLeft: '3px',
    paddingTop: '7px',
    paddingBottom: '5px',
    borderTop: 'none',
  },
  UserProfile: {
    margin: '0',
    backgroundColor: '#F2F2F2',
    color: '#5c5c5c',
    border: 'none',
    alignContent: 'left',
    paddingBottom: '10px',
    paddingTop: '8px',
    marginTop: 'none',
    paddingLeft: '3px',
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '13.31px',
    marginBottom: '-10px',
    width: '100%',
    borderBottom: '1.5px solid #E6E6E6',
    paddingRight: '50px',
  },
  SupportButton: {
    backgroundColor: 'transparent',
    color: 'black',
    padding: '10px',
    border: 'none',
    alignContent: 'left',
    fontSize: '14px',
    fontFamily: 'Inter',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
    },
  },
  halfLineSpace: {
    display: 'block',
    margin: '10px 0',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '10px',
  },
  logoWrapper: {
    marginBottom: '5px',
  },
  breadcrumbWrapper: {
    borderRadius: '4px',
    marginLeft: '-120px',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '26px',
    textDecoration: 'none',
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: '4px',
    width: '120px',
  },
  breadcrumbLink: {
    marginTop: '10px',
    textDecoration: 'none',
    color: '#0066B0',
    marginRight: '8px',
    borderRadius: '4px',
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '14.52px',
    cursor: 'pointer',
  },
  breadcrumbSeparator: {
    marginTop: '10px',
    marginRight: '8px',
    color: '#3F3F4A',
  },
  activeBreadcrumbLink: {
    marginTop: '10px',
    marginRight: '8px',
    borderRadius: '4px',
    textDecoration: 'none',
    color: '#3F3F4A',
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '14.52px',
    cursor: 'pointer',
  },
  aboutOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(0,0,0,0.3)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  aboutModal: {
    background: 'white',
    borderRadius: 8,
    padding: 20,
    minWidth: 320,
  },
  logout: {
    width: '100%',
    background: 'none',
    border: 'none',
    padding: 0,
    color: 'inherit',
    font: 'inherit',
    cursor: 'pointer',
    display: 'block',
  },
  aboutModalButton: {
    position: 'absolute' as const,
    top: 8,
    right: 8,
    zIndex: 2,
  },
  helpDropdown: {
    position: 'relative' as const,
    display: 'inline-block',
  },
};
